import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { LocalComponentStore } from '../store/local-component.store';
import { AppSettings } from '../app.settings';
import { Response } from '../models/response.model';
import { Notification } from '../models/notification.model';
import {isEmptyArray} from '../shared/utils';
import {API_URL} from '../constants/api-urls';

const DEFAULT_PAGE_SIZE = 10;

interface NotificationState {
    totalNotifications: number;
    notifications: Notification[];
    fetching: boolean;
}

/** Groups notifications by their formatted creation date (preserves former @computed behaviour). */
function buildSortedNotifications(notifications: Notification[]): Record<string, Notification[]> {
    if (isEmptyArray(notifications)) {
        return {};
    }

    const mapNotificationsByDate = (obj: Record<string, Notification[]>, notification: Notification) => {
        const formattedDate = notification.created_at.longDateFormatted;
        const notificationsList = obj[formattedDate];
        if (notificationsList) {
            obj[formattedDate] = [...notificationsList, notification];
        } else {
            obj[formattedDate] = [notification];
        }
    };

    const result: Record<string, Notification[]> = {};
    notifications.forEach( n => {
        mapNotificationsByDate(result, n);
    });

    return result;
}

@Injectable()
export class NotificationService extends BaseService {

    private store = new LocalComponentStore<NotificationState>({
        totalNotifications: 0,
        notifications: [],
        fetching: false,
    });

    /** Reactive streams for templates (replace the former `*mobxAutorun` reads). */
    readonly notifications$ = this.store.select(s => s.notifications);
    readonly fetching$ = this.store.select(s => s.fetching);
    // derive from notifications$ so it only recomputes when notifications change (not on every state change)
    readonly sortedNotifications$ = this.notifications$.pipe(map(buildSortedNotifications));

    /** Synchronous accessors preserve the previous public observable fields. */
    get totalNotifications(): number {
        return this.store.snapshot(s => s.totalNotifications);
    }
    set totalNotifications(value: number) {
        this.store.patchState({ totalNotifications: value });
    }

    get notifications(): Notification[] {
        return this.store.snapshot(s => s.notifications);
    }
    set notifications(value: Notification[]) {
        this.store.patchState({ notifications: value });
    }

    get fetching(): boolean {
        return this.store.snapshot(s => s.fetching);
    }
    set fetching(value: boolean) {
        this.store.patchState({ fetching: value });
    }

    constructor(
        protected http: HttpClient,
        protected appSettings: AppSettings,
        protected toastr: ToastrService
    ) {
        super('/account/notifications', http, appSettings, toastr);
    }

    get(fechtingMore = false): Observable<Notification[]> {

        if (!fechtingMore && !isEmptyArray(this.notifications)) {
           return of(this.notifications);
        }

        if (fechtingMore && !this.hasMoreNotifications) {
            // no notifications more are available
            return of([]);
        }

        this.fetching = true;
        const paging = `${DEFAULT_PAGE_SIZE}-${fechtingMore ? this.notifications.length : 0}`;
        const headers: HttpHeaders = new HttpHeaders({'Range': paging});
        const url = `${API_URL}/account/notifications`;
        return this.http.get<HttpResponse<Response>>(url, {observe: 'response', headers: headers})
            .pipe(
                map(httpResponse => {
                    this.totalNotifications = parseInt(httpResponse.headers.get('X-Count'), 10) || 0;
                    const response = new Response(httpResponse.body);
                    if (!isEmptyArray(response.data)) {
                        return response.data.map( d => new Notification(d));
                    }
                    return [];
                }),
                catchError(this.handleError('NotificationService::get', [])),
                tap( notifications => {
                    if (fechtingMore) {
                        if (!isEmptyArray(notifications)) {
                            this.setNotifications([...this.notifications, ...notifications]);
                        }
                    } else {
                        this.setNotifications(notifications);
                    }

                }),
                finalize( () => {
                    this.fetching = false;
                })
            );
    }

    unreadCount(): Observable<number> {
        const url = `${API_URL}/account/notifications/unread-count`;
        return this.http.get<Response>(url)
            .pipe(
                map(response => {
                    if (response.data) {
                        return response.data.count;
                    }
                    return 0;
                }),
                catchError(this.handleError('NotificationService::unreadCount', 0))
            );
    }


    setNotifications(val: Notification[] = []) {
        this.notifications = val;
    }

    get hasMoreNotifications(): boolean {
        return this.totalNotifications > this.notifications.length;
    }

    get sortedNotifications(): Record<string, Notification[]> {
        return buildSortedNotifications(this.notifications);
    }
}
